import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { atom, useSetAtom } from 'jotai';
import { redirectTo } from 'signer-app/utils/redirect';
import { useReady } from 'signer-app/utils/signer-app-ready';
import { unreachable } from 'signer-app/utils/unreachable';
import type { AccessCodeResponse } from './use-signer-auth';

export const readyErrorAtom = atom(false);

type UseSignerReadyParams = {
  tsmGuid: string;
  accessCode: string | null;
  setAuthState: React.Dispatch<React.SetStateAction<AccessCodeResponse | null>>;
  setSmsMeta: (meta: any | null) => void;
  setNumAttemptsLeft: (num: number) => void;
};

export const MAX_RETRY_DELAY = 4000;
// sum up to 4 minutes on retry delay
export const MAX_RETRY_COUNT = 60;
export const OTHER_ERROR_RETRY_COUNT = 3;

export class SignerNotReadyError extends Error {
  constructor() {
    super('Signer app not ready yet');
    this.name = 'SignerNotReadyError';
    // ES5 transpilation breaks prototype chain for Error subclasses,
    // making instanceof checks fail. See the .name check pattern used
    // for ResponseError in use-fetch-signature-request.ts / use-finalize.ts.
    Object.setPrototypeOf(this, SignerNotReadyError.prototype);
  }
}

export function useSignerReady({
  tsmGuid,
  accessCode,
  setAuthState,
  setSmsMeta,
  setNumAttemptsLeft,
}: UseSignerReadyParams) {
  const ready = useReady();
  const setReadyError = useSetAtom(readyErrorAtom);

  const { data: readyData, isFetching: readyIsFetching } = useQuery({
    queryKey: ['signer-experience', 'ready', tsmGuid, accessCode] as const,
    queryFn: async () => {
      setReadyError(false);
      const codeToSend = accessCode ?? undefined;
      const response = await ready({ tsmGuid, accessCode: codeToSend });

      if (response.isInFlightEditing) {
        redirectTo('/info/externalCantSignNow');
      }

      // Throw error to trigger retry when not ready and no auth challenge
      // This leverages React Query's built-in retry mechanism
      // Check response.error?.code because backend returns error: {} when no error
      if (!response.ready && !response.error?.code) {
        // Clear auth state before throwing - prevents stale auth errors when user
        // submits correct code but document isn't ready yet (throwing bypasses onSuccess)
        setAuthState(null);
        setNumAttemptsLeft(0);
        throw new SignerNotReadyError();
      }

      return response;
    },
    onSuccess(readyResponseData) {
      const code = readyResponseData?.error?.code;

      if (
        (code === 'sms_rate_limit' || code === 'sms_auth_required') &&
        readyResponseData.error?.data
      ) {
        setSmsMeta(readyResponseData.error?.data);
      }
      const newAttemptsLeft =
        readyResponseData.error?.data?.numAttemptsLeft ?? 0;
      setNumAttemptsLeft(newAttemptsLeft);

      if (code === 'sms_rate_limit') {
        setAuthState('sms-rate-limit');
        return;
      }

      setAuthState((prev: AccessCodeResponse | null) => {
        if (prev === 'sms-rate-limit') {
          return prev;
        }

        switch (code) {
          case undefined:
            return null;
          case 'sms_auth_required':
            return 'need-sms-auth';
          case 'access_code_required':
            return accessCode !== null
              ? 'invalid-access-code'
              : 'need-access-code';
          case 'access_code_blocked':
          case 'too_many_requests':
            return 'too-many-retries';
          case 'need_signer_choice':
            return prev;
          default:
            unreachable(code);
            return prev;
        }
      });
    },
    onError() {
      setReadyError(true);
    },
    retry: (attemptIndex, error) => {
      if (error instanceof SignerNotReadyError) {
        return attemptIndex < MAX_RETRY_COUNT;
      }

      return attemptIndex < OTHER_ERROR_RETRY_COUNT;
    },
    retryDelay: (attemptIndex) => {
      return Math.min(2000 * 2 ** attemptIndex, MAX_RETRY_DELAY);
    },
  });

  return {
    readyData,
    readyIsFetching,
  };
}
